import { useEffect } from "react";

import { faSparkles } from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import { twJoin } from "tailwind-merge";
import { useLocalStorage } from "usehooks-ts";

import type { Flow } from "src/api/flowTypes";
import { Button } from "src/design-system/Button";
import { Icon } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";
import { useLiveAnalyticsAccess } from "src/flow/analytics/useLiveAnalyticsAccess";
import { useFlowNavSidebarCollapsed } from "src/flow/useFlowNavSidebarCollapsed";
import { useCapabilities } from "src/hooks/useCapabilities";
import { flowFolderScope } from "src/hooks/useCapabilities/flowFolderScope";
import { type FlowPageParams, getFlowSubpageUrl } from "src/router/urls";
import { useCurrentPage } from "src/router/useCurrentPage";
import { useIsTaktileStaffEmail } from "src/store/AuthStore";
import { useParamsDecode } from "src/utils/useParamsDecode";

type IntroModalProps = {
  onClose: () => void;
  onConfirm: () => void;
};

const EXAMPLE_QUESTION =
  "Why did the approval rate drop after the v14 release?";

const ANSWER_TITLE = "Approval rate \u00b7 v13 vs v14";
const ANSWER_DELTA = "\u22128.2 pts";
// What a version-grouped query can always back: when the drop starts and how
// many decisions moved. Attributing it to a rule change would need the agent to
// diff the flow itself, which is not what it reads.
const ANSWER_EXPLANATION =
  "The drop starts with v14: 214 decisions changed outcome.";

type RateBar = {
  key: string;
  height: string;
  color: string;
};

// A still life of the chart Insights returns for EXAMPLE_QUESTION: the approval
// rate holding steady on v13, then stepping down across v14. Volume charts are
// the Performance tab's job, so the preview shows an outcome instead.
const RATE_BARS: RateBar[] = [
  { key: "v13-1", height: "h-16", color: "bg-indigo-200" },
  { key: "v13-2", height: "h-14", color: "bg-indigo-200" },
  { key: "v13-3", height: "h-15", color: "bg-indigo-200" },
  { key: "v14-1", height: "h-10", color: "bg-indigo-600" },
  { key: "v14-2", height: "h-9", color: "bg-indigo-600" },
  { key: "v14-3", height: "h-10", color: "bg-indigo-600" },
];

const IntroModal: React.FC<IntroModalProps> = ({ onClose, onConfirm }) => (
  <Modal open size="md" onClose={onClose}>
    <Modal.Header description="Monitor, report and investigate your decisions. Ask in plain language, pin the answers to a live dashboard.">
      Insights is now available for your flows
    </Modal.Header>
    <Modal.Content>
      {/* Pulls the preview up under the header: Modal.Header's pb-3 plus
          Modal.Content's p-5 otherwise leave a 32px gap. */}
      <div className="-mt-2 flex flex-col gap-y-2.5">
        <div className="flex items-center gap-x-1.5 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2">
          <Icon icon={faSparkles} size="xs" variant="active" />
          <span className="font-inter-normal-13px text-gray-800">
            {EXAMPLE_QUESTION}
          </span>
        </div>
        <div className="rounded-lg border border-gray-200 px-3.5 py-3">
          <div className="mb-2.5 flex items-center gap-x-2">
            <span className="font-inter-semibold-12px text-gray-800">
              {ANSWER_TITLE}
            </span>
            <span className="ml-auto font-inter-semibold-12px text-red-600">
              {ANSWER_DELTA}
            </span>
          </div>
          <div className="flex h-16 items-end gap-x-1.5">
            {RATE_BARS.map(({ key, height, color }) => (
              <span
                key={key}
                className={twJoin("flex-1 rounded-t", height, color)}
              />
            ))}
          </div>
          <div className="mt-1 flex font-inter-medium-11px text-gray-400">
            <span className="flex-1 text-center">v13</span>
            <span className="flex-1 text-center">v14</span>
          </div>
          <div className="mt-2.5 flex items-start gap-x-1.5 border-gray-100 border-t pt-2.5">
            <Icon icon={faSparkles} size="2xs" variant="active" />
            <span className="font-inter-normal-12px text-gray-600">
              {ANSWER_EXPLANATION}
            </span>
          </div>
        </div>
      </div>
    </Modal.Content>
    {/* Always-on divider per the modal spec: Modal.Content only draws its
        own bottom border while the content scrolls. */}
    <div className="border-gray-200 border-t" />
    <Modal.Footer
      secondaryButton={false}
      primaryButton={
        <Button
          dataLoc="flow-insights-intro-confirm"
          variant="primary"
          onClick={onConfirm}
        >
          Explore Insights
        </Button>
      }
    />
  </Modal>
);

export const FlowInsightsIntroModal: React.FC<{ flow: Flow | undefined }> = ({
  flow,
}) => {
  const { orgId, wsId, flow_id: flowId } = useParamsDecode<FlowPageParams>();
  const navigate = useNavigate();
  const { canAccess } = useLiveAnalyticsAccess();
  const { flows } = useCapabilities();
  const { isFlowInsightsPage } = useCurrentPage();
  const isTaktileStaff = useIsTaktileStaffEmail();
  // Unchanged since the first release on purpose. Anyone who dismissed the
  // modal before it was redesigned holds this flag, and version-bumping the key
  // is how you would re-show the modal to every one of them.
  const [introSeen, setIntroSeen] = useLocalStorage(
    `flowInsightsIntroSeen:${wsId}`,
    false,
  );

  const canOpenInsights =
    canAccess && flow !== undefined && flows.canEdit(flowFolderScope(flow));
  const needsIntro = canOpenInsights && !isTaktileStaff;
  const showIntro = needsIntro && !introSeen && !isFlowInsightsPage;
  const [, setSidebarCollapsed] = useFlowNavSidebarCollapsed();

  useEffect(() => {
    if (needsIntro && isFlowInsightsPage) {
      setIntroSeen(true);
    }
  }, [needsIntro, isFlowInsightsPage, setIntroSeen]);

  useEffect(() => {
    if (showIntro) {
      setSidebarCollapsed(false);
    }
  }, [showIntro, setSidebarCollapsed]);

  if (!showIntro) {
    return null;
  }

  const handleClose = () => setIntroSeen(true);
  const handleConfirm = () => {
    setIntroSeen(true);
    navigate(getFlowSubpageUrl(orgId, wsId, flowId, "insights"));
  };

  return <IntroModal onClose={handleClose} onConfirm={handleConfirm} />;
};
